<template>
  <section id="tool" class="py-24 relative overflow-hidden bg-[#1A1A1F]">
    <div class="mesh-bg" style="width:500px;height:500px;background:radial-gradient(circle,rgba(255,92,59,0.10),transparent 70%);top:0;right:-100px;"></div>

    <div class="max-w-7xl mx-auto px-6">
      <!-- Section header -->
      <div v-if="currentState !== 'preview'" class="text-center mb-14 animate-fade-in">
        <div class="badge mx-auto">🔥 Live Generator & Editor</div>
        <h2 class="mt-5 text-white" style="font-size:clamp(2rem,4vw,3rem);font-weight:800;letter-spacing:-0.03em;font-family:'Outfit',sans-serif;">
          Create Your Presentation<br/><span class="gradient-text">Right Now</span>
        </h2>
        <p class="mt-4" style="color:rgba(245,240,232,0.55);max-width:480px;margin-inline:auto;">
          Enter a topic, pick your style, and let SlideForge AI do the heavy lifting.
        </p>
      </div>
      <!-- 1. LANDING & FORM GENERATOR VIEW -->
      <div v-if="currentState === 'placeholder' || currentState === 'loading'" class="grid lg:grid-cols-5 gap-8 items-start animate-fade-in">
        <!-- Tool Form: col-span-2 -->
        <div class="lg:col-span-2 text-left">
          <div class="glass rounded-3xl p-8 space-y-6" style="border-color:rgba(255,255,255,0.1);">
            <!-- Topic input -->
            <div>
              <label class="block text-sm mb-2" style="color:rgba(245,240,232,0.75);font-weight:600;font-family:'Outfit',sans-serif;">
                Presentation Topic
              </label>
              <input
                v-model="topic"
                type="text"
                class="sf-input w-full rounded-xl px-5 py-3.5 text-base text-white bg-black/35 focus:ring-1 focus:ring-orange-500"
                :class="{ 'shake': isShaking }"
                :style="topicError ? { borderColor: 'var(--coral)' } : {}"
                placeholder="e.g. Artificial Intelligence in Health"
                @keydown.enter="generatePresentation"
              />
              <div v-if="topicError" class="mt-2 text-sm text-[#FF5C3B] flex items-center gap-1">
                ⚠ Please enter a topic first!
              </div>
            </div>

            <!-- Template style -->
            <div>
              <label class="block text-sm mb-2" style="color:rgba(245,240,232,0.75);font-weight:600;font-family:'Outfit',sans-serif;">
                Template Style
              </label>
              <select v-model="templateStyle" class="sf-select w-full rounded-xl px-5 py-3.5 text-base">
                <option value="business">🏢 Business Professional</option>
                <option value="startup">🚀 Startup Pitch</option>
                <option value="education">🎓 Education & Academic</option>
                <option value="marketing">📣 Marketing & Sales</option>
                <option value="minimal">◽ Clean Minimal</option>
                <option value="dark">🌑 Dark Premium</option>
              </select>
            </div>

            <!-- Slide count -->
            <div>
              <label class="block text-sm mb-2" style="color:rgba(245,240,232,0.75);font-weight:600;font-family:'Outfit',sans-serif;">
                Number of Slides
              </label>
              <select v-model="slidesCount" class="sf-select w-full rounded-xl px-5 py-3.5 text-base">
                <option value="5">5 Slides (Quick)</option>
                <option value="10">10 Slides (Standard)</option>
                <option value="15">15 Slides (Detailed)</option>
                <option value="20">20 Slides (Comprehensive)</option>
              </select>
            </div>

            <!-- Toggles -->
            <div class="space-y-4 py-2">
              <div class="flex items-center justify-between">
                <div>
                  <div style="font-size:0.9rem;font-weight:600;font-family:'Outfit',sans-serif;color:white;">Speaker Notes</div>
                  <div style="font-size:0.75rem;color:rgba(245,240,232,0.45);">Generate notes for each slide</div>
                </div>
                <label class="toggle-wrap">
                  <input type="checkbox" class="toggle-input" v-model="speakerNotes" />
                  <span class="toggle-slider"></span>
                </label>
              </div>
              <div style="height:1px;background:rgba(255,255,255,0.06);"></div>
              <div class="flex items-center justify-between">
                <div>
                  <div style="font-size:0.9rem;font-weight:600;font-family:'Outfit',sans-serif;color:white;">Professional Tone</div>
                  <div style="font-size:0.75rem;color:rgba(245,240,232,0.45);">Formal, executive-ready language</div>
                </div>
                <label class="toggle-wrap">
                  <input type="checkbox" class="toggle-input" v-model="professionalTone" />
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>

            <!-- Additional AI Prompt instructions -->
            <div>
              <label class="block text-sm mb-2" style="color:rgba(245,240,232,0.75);font-weight:600;font-family:'Outfit',sans-serif;">
                Additional Instructions (Optional)
              </label>
              <textarea
                v-model="additionalInstructions"
                class="w-full rounded-xl px-4 py-3 text-xs text-white bg-black/35 border border-white/10 focus:outline-none focus:border-orange-500/50 min-h-16"
                placeholder="e.g. Focus heavily on marketing ROI, write in simple bullet points, or list a real-world hack..."
                style="resize:vertical;"
              ></textarea>
            </div>

            <!-- Generate Button -->
            <button id="generate-btn" class="btn-primary w-full py-4 rounded-2xl text-base flex items-center justify-center gap-3" @click="generatePresentation">
              <span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </span>
              <span>Generate Presentation</span>
            </button>
          </div>
        </div>

        <!-- Preview Column: Placeholder and Loading states -->
        <div class="lg:col-span-3 h-full">
          <!-- Placeholder state -->
          <div v-if="currentState === 'placeholder'" class="glass rounded-3xl p-8 h-full flex flex-col items-center justify-center text-center min-h-[480px]" style="border:2px dashed rgba(255,255,255,0.08);">
            <div class="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style="background:rgba(255,92,59,0.1);">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect x="2" y="6" width="28" height="20" rx="2" stroke="rgba(255,92,59,0.6)" stroke-width="2"/><line x1="8" y1="12" x2="24" y2="12" stroke="rgba(255,92,59,0.4)" stroke-width="2"/><line x1="8" y1="17" x2="20" y2="17" stroke="rgba(255,92,59,0.3)" stroke-width="2"/><line x1="8" y1="22" x2="16" y2="22" stroke="rgba(255,92,59,0.2)" stroke-width="2"/></svg>
            </div>
            <h3 style="font-size:1.25rem;font-weight:700;margin-bottom:8px;color:white;font-family:'Outfit',sans-serif;">Your slides will appear here</h3>
            <p style="color:rgba(245,240,232,0.45);font-size:0.9rem;max-width:320px;">Enter your topic and click "Generate Presentation" to see the magic happen instantly.</p>
            <div class="mt-6 flex gap-2 opacity-40">
              <div class="w-16 h-10 rounded-lg" style="background:var(--char-7);"></div>
              <div class="w-16 h-10 rounded-lg" style="background:var(--char-7);"></div>
              <div class="w-16 h-10 rounded-lg" style="background:var(--char-7);"></div>
              <div class="w-16 h-10 rounded-lg hidden sm:block" style="background:var(--char-7);"></div>
            </div>
          </div>

          <!-- Loading state -->
          <div v-else-if="currentState === 'loading'" class="glass rounded-3xl p-8 h-full flex flex-col items-center justify-center text-center min-h-[480px]" style="border-color:rgba(255,92,59,0.2);">
            <div class="spinner mx-auto mb-5"></div>
            <h3 style="font-size:1.1rem;font-weight:700;margin-bottom:6px;color:white;font-family:'Outfit',sans-serif;">{{ loadingLabel }}</h3>
            <p style="color:rgba(245,240,232,0.45);font-size:0.85rem;">SlideForge AI is designing your slides</p>
            <div class="mt-6 w-full max-w-xs mx-auto rounded-full overflow-hidden" style="height:4px;background:rgba(255,255,255,0.08);">
              <div class="progress-bar h-full rounded-full" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <div class="mt-5 space-y-2 text-left max-w-xs mx-auto text-sm" style="color:rgba(245,240,232,0.5);">
              <div class="flex items-center gap-2" :style="{ opacity: stepStatuses.step1.opacity }">
                <span :style="{ color: stepStatuses.step1.color }">{{ stepStatuses.step1.icon }}</span> Analyzing topic…
              </div>
              <div class="flex items-center gap-2" :style="{ opacity: stepStatuses.step2.opacity }">
                <span :style="{ color: stepStatuses.step2.color }">{{ stepStatuses.step2.icon }}</span> Generating outline…
              </div>
              <div class="flex items-center gap-2" :style="{ opacity: stepStatuses.step3.opacity }">
                <span :style="{ color: stepStatuses.step3.color }">{{ stepStatuses.step3.icon }}</span> Designing slides…
              </div>
              <div class="flex items-center gap-2" :style="{ opacity: stepStatuses.step4.opacity }">
                <span :style="{ color: stepStatuses.step4.color }">{{ stepStatuses.step4.icon }}</span> Applying template…
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 2. PREMIUM EDITOR WORKSPACE DASHBOARD VIEW -->
      <div v-else-if="currentState === 'preview'" class="glass rounded-3xl border border-white/10 overflow-hidden shadow-2xl animate-fade-in text-left">
        <!-- Dashboard Top Header Bar -->
        <div class="px-6 py-4 flex flex-wrap items-center justify-between border-b border-white/10" style="background:rgba(255,255,255,0.02);">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white bg-gradient-to-br from-orange-500 to-coral-600 text-sm shadow-md">SF</div>
            <div>
              <h3 class="text-sm font-bold text-white leading-tight font-display">{{ previewData.title }}</h3>
              <p class="text-[10px] text-gray-400 mt-0.5">{{ previewData.meta }}</p>
            </div>
          </div>
          <div class="flex items-center gap-4 mt-2 sm:mt-0">
            <!-- Active Slide Index indicator -->
            <span class="text-xs font-bold text-gray-400">Slide {{ selectedSlideIndex + 1 }} of {{ previewData.slides.length }}</span>
            <div class="h-4 w-px bg-white/10"></div>
            <!-- Download & Action Buttons -->
            <div class="flex items-center gap-2">
              <button @click="exportToPptx" :disabled="downloadStates.pptx.disabled" class="btn-primary py-2 px-4 rounded-xl text-xs flex items-center gap-1.5 shadow-lg shadow-orange-500/10">
                <span v-html="downloadStates.pptx.html"></span>
              </button>
              <button @click="fakeDownload('pdf')" class="btn-outline py-2 px-4 rounded-xl text-xs flex items-center gap-1.5">
                <span v-html="downloadStates.pdf.html"></span>
              </button>
              <button @click="resetTool" class="text-xs font-semibold text-gray-400 hover:text-white px-3 transition-colors">
                ↺ Start Over
              </button>
            </div>
          </div>
        </div>

        <!-- Dashboard Workspace Grid (Left Panel, Center Canvas, Right Panel) -->
        <div class="grid lg:grid-cols-12 gap-0 min-h-[580px]">
          
          <!-- LEFT SIDEBAR PANEL: Design Controls & AI Image Generator -->
          <div class="lg:col-span-3 border-r border-white/10 p-5 space-y-6 text-left" style="background:rgba(0,0,0,0.12);">
            <!-- Section Title -->
            <div class="text-xs font-bold text-gray-400 uppercase tracking-widest pb-2 border-b border-white/5">Slide Settings</div>
            
            <!-- Slide Layout Selector -->
            <div>
              <label class="block text-xs font-semibold text-gray-300 mb-2">Slide Layout Format</label>
              <select v-model="previewData.slides[selectedSlideIndex].type" class="w-full bg-black/45 text-white border border-white/10 rounded-xl px-4 py-2.5 text-xs focus:ring-1 focus:ring-orange-500">
                <option value="title">🎨 Cover/Title Slide</option>
                <option value="content">🏢 Key Columns Grid</option>
                <option value="split">🌗 Image Split Panel</option>
                <option value="conclusion">✓ Conclusion Checklist</option>
              </select>
            </div>

            <!-- Dynamic Style Theme Tiles -->
            <div>
              <label class="block text-xs font-semibold text-gray-300 mb-2">Template Styling Theme</label>
              <div class="grid grid-cols-3 gap-2">
                <button v-for="(theme, key) in templateThemes" 
                        :key="key" 
                        @click="changeTheme(key)"
                        class="p-2 rounded-lg border text-[10px] text-center font-semibold transition-all duration-300"
                        :class="templateStyle === key ? 'border-orange-500 ring-2 ring-orange-500/10' : 'border-white/10 hover:border-white/20'"
                        :style="{ background: theme.bg, color: theme.text }">
                  {{ key.charAt(0).toUpperCase() + key.slice(1) }}
                </button>
              </div>
            </div>

            <!-- AI IMAGE GENERATOR ASSISTANT (COMING SOON) -->
            <div class="space-y-3 pt-4 border-t border-white/5">
              <div class="text-xs font-bold text-gray-500 uppercase tracking-widest pb-1 flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="opacity-50"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <span>AI Image Generator</span>
              </div>
              <div class="p-4 rounded-xl border border-white/5 bg-black/20 text-center space-y-1">
                <div class="text-xs font-bold text-orange-400/80">Coming Soon</div>
                <div class="text-[9px] text-gray-500">Auto-generated smart stock graphics and custom AI designs will be available in the next version.</div>
              </div>
            </div>
          </div>

          <!-- CENTER CANVAS PANEL: The Widescreen presentation slide -->
          <div class="lg:col-span-6 p-6 flex flex-col justify-between" style="background:rgba(255,255,255,0.01);">
            
            <!-- Slide Widescreen Canvas -->
            <div class="slide-thumb w-full rounded-2xl p-8 relative overflow-hidden transition-all duration-300 shadow-xl border border-white/5" 
                 :style="{ background: previewData.theme.bg, color: previewData.theme.text }"
                 style="min-height:380px;">
              <div style="position:absolute;inset:0;background:radial-gradient(ellipse at 65% 35%,rgba(255,255,255,0.04),transparent 60%);pointer-events:none;"></div>
              
              <!-- Slide Layout Rendering -->
              
              <!-- 1. TITLE LAYOUT -->
              <div v-if="previewData.slides[selectedSlideIndex].type === 'title'" class="h-full flex flex-col justify-between relative z-10 text-left pt-2">
                <div>
                  <div :style="{ color: previewData.theme.accent }" class="text-xs uppercase font-bold tracking-wider mb-2" style="font-family:'Outfit',sans-serif;">
                    {{ previewData.theme.category }}
                  </div>
                  <!-- Inline Editable Title -->
                  <h2 contenteditable="true" 
                      @blur="updateSlideTitle($event, selectedSlideIndex)" 
                      class="text-3xl font-extrabold focus:outline-none focus:ring-1 focus:ring-orange-400/50 p-1 rounded transition-colors max-w-[85%] leading-tight" 
                      style="font-family:'Outfit',sans-serif;"
                      v-html="previewData.slides[selectedSlideIndex].title">
                  </h2>
                  <p class="text-sm mt-3 opacity-60">A dynamic presentation curated with SlideForge AI.</p>
                </div>
                
                <div class="mt-8 flex items-center gap-3">
                  <div :style="{ background: previewData.theme.accent }" style="height:2px;width:30px;border-radius:2px;"></div>
                  <span class="text-xs opacity-50 font-sans">SlideForge AI · {{ new Date().getFullYear() }}</span>
                </div>
              </div>

              <!-- 2. SPLIT LAYOUT (Left text description, Right columns) -->
              <div v-else-if="previewData.slides[selectedSlideIndex].type === 'split'" class="grid md:grid-cols-2 gap-6 h-full relative z-10 text-left pt-2">
                <div>
                  <h2 contenteditable="true" 
                      @blur="updateSlideTitle($event, selectedSlideIndex)" 
                      class="text-2xl font-bold focus:outline-none focus:ring-1 focus:ring-orange-400/50 p-1 rounded leading-tight" 
                      style="font-family:'Outfit',sans-serif;"
                      v-html="previewData.slides[selectedSlideIndex].title">
                  </h2>
                  <p class="text-xs mt-3 opacity-65 leading-relaxed">Analyzing the core principles, data evidence, and pillars supporting this concept statement.</p>
                </div>
                
                <div class="space-y-2.5">
                  <div v-for="(bullet, bIdx) in previewData.slides[selectedSlideIndex].bullets" 
                       :key="bIdx" 
                       class="p-2.5 rounded-lg relative flex gap-2 items-start group border" 
                       :style="{ background: previewData.theme.cardBg, borderColor: previewData.theme.accent + '22' }">
                    <span :style="{ color: previewData.theme.accent }" class="font-bold text-xs mt-0.5">0{{ bIdx + 1 }}</span>
                    <div contenteditable="true" 
                         @blur="updateSlideBullet($event, selectedSlideIndex, bIdx)" 
                         class="text-xs focus:outline-none focus:ring-1 focus:ring-orange-400/50 rounded p-0.5 flex-1 pr-6"
                         v-html="bullet">
                    </div>
                    <button @click="deleteBullet(selectedSlideIndex, bIdx)" class="absolute right-2 top-2 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-500">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                  <button @click="addBullet(selectedSlideIndex)" class="text-[10px] font-semibold py-1 px-3 rounded border border-dashed flex items-center gap-1.5 opacity-60 hover:opacity-100" :style="{ borderColor: previewData.theme.accent, color: previewData.theme.accent }">
                    + Add Item
                  </button>
                </div>
              </div>

              <!-- 3. CONCLUSION LAYOUT -->
              <div v-else-if="previewData.slides[selectedSlideIndex].type === 'conclusion'" class="h-full flex flex-col justify-between relative z-10 text-left">
                <div class="text-center pt-2">
                  <h2 contenteditable="true" 
                      @blur="updateSlideTitle($event, selectedSlideIndex)" 
                      class="text-2xl font-bold focus:outline-none focus:ring-1 focus:ring-orange-400/50 p-1 rounded inline-block text-center" 
                      style="font-family:'Outfit',sans-serif;"
                      v-html="previewData.slides[selectedSlideIndex].title">
                  </h2>
                </div>
                
                <div class="max-w-2xl mx-auto w-full my-4">
                  <!-- Text bullets -->
                  <div class="p-5 rounded-xl border space-y-3 w-full" :style="{ background: previewData.theme.cardBg, borderColor: previewData.theme.accent + '22' }">
                    <div v-for="(bullet, bIdx) in previewData.slides[selectedSlideIndex].bullets" 
                         :key="bIdx" 
                         class="flex items-start gap-2.5 text-xs relative group">
                      <span :style="{ color: previewData.theme.accent }">✓</span>
                      <div contenteditable="true" 
                           @blur="updateSlideBullet($event, selectedSlideIndex, bIdx)" 
                           class="focus:outline-none focus:ring-1 focus:ring-orange-400/50 rounded p-0.5 flex-1 pr-6"
                           v-html="bullet">
                      </div>
                      <button @click="deleteBullet(selectedSlideIndex, bIdx)" class="absolute right-2 top-0.5 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-500">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                    <button @click="addBullet(selectedSlideIndex)" class="text-[10px] font-semibold py-1 px-3 rounded border border-dashed flex items-center gap-1.5 opacity-60 hover:opacity-100" :style="{ borderColor: previewData.theme.accent, color: previewData.theme.accent }">
                      + Add Key Takeaway
                    </button>
                  </div>
                </div>

                <div class="text-center font-bold text-sm tracking-wide opacity-80" :style="{ color: previewData.theme.accent }">
                  Thank You for Your Time!
                </div>
              </div>

              <!-- 4. STANDARD CONTENT LAYOUT -->
              <div v-else class="h-full flex flex-col relative z-10 text-left pt-1">
                <!-- Title bar -->
                <div class="flex items-center gap-2.5 mb-4">
                  <div :style="{ background: previewData.theme.accent }" style="height:18px;width:3px;border-radius:1px;"></div>
                  <h2 contenteditable="true" 
                      @blur="updateSlideTitle($event, selectedSlideIndex)" 
                      class="text-2xl font-bold focus:outline-none focus:ring-1 focus:ring-orange-400/50 p-1 rounded" 
                      style="font-family:'Outfit',sans-serif;"
                      v-html="previewData.slides[selectedSlideIndex].title">
                  </h2>
                </div>

                <!-- Dynamic columns bullets -->
                <div class="grid gap-4 items-stretch h-full flex-1" :class="gridColumnsClass">
                  <!-- Bullets columns -->
                  <div v-for="(bullet, bIdx) in previewData.slides[selectedSlideIndex].bullets" 
                       :key="bIdx" 
                       class="p-4 rounded-xl relative flex flex-col justify-between group border transition-all duration-300"
                       :style="{ background: previewData.theme.cardBg, borderColor: previewData.theme.accent + '15' }">
                    <div class="text-left w-full">
                      <div :style="{ background: previewData.theme.accent }" style="height:3px;width:24px;border-radius:1.5px;margin-bottom:8px;"></div>
                      <div :style="{ color: previewData.theme.accent }" class="text-[0.65rem] font-bold uppercase tracking-wider mb-2">Key Point 0{{ bIdx + 1 }}</div>
                      <div contenteditable="true" 
                           @blur="updateSlideBullet($event, selectedSlideIndex, bIdx)" 
                           class="text-xs focus:outline-none focus:ring-1 focus:ring-orange-400/50 rounded p-0.5 leading-relaxed"
                           v-html="bullet">
                      </div>
                    </div>
                    <button @click="deleteBullet(selectedSlideIndex, bIdx)" class="absolute right-3 top-3 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                </div>
                
                <div class="mt-4 flex items-center justify-between">
                  <button @click="addBullet(selectedSlideIndex)" class="text-[10px] font-semibold py-1.5 px-3.5 rounded border border-dashed flex items-center gap-1.5 opacity-60 hover:opacity-100" :style="{ borderColor: previewData.theme.accent, color: previewData.theme.accent }">
                    + Add Key Column
                  </button>
                  <span class="text-[10px] opacity-40">Slide {{ selectedSlideIndex + 1 }}</span>
                </div>
              </div>
            </div>

            <!-- Slide Speaker Notes pane -->
            <div class="glass rounded-xl p-4 border border-white/5 space-y-2 mt-4">
              <div class="flex items-center gap-2 text-xs font-semibold text-gray-400">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <span>SPEAKER SCRIP NOTES</span>
              </div>
              <textarea v-model="previewData.slides[selectedSlideIndex].speaker_notes" 
                        placeholder="Type script notes for the presenter here..." 
                        class="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-xs text-gray-300 focus:outline-none focus:border-orange-500/40 min-h-16"
                        style="resize:vertical;"></textarea>
            </div>
          </div>

          <!-- RIGHT DECK MANAGER PANEL: Slide Thumbnail Cards & Reordering -->
          <div class="lg:col-span-3 border-l border-white/10 p-5 space-y-4 text-left" style="background:rgba(0,0,0,0.12);">
            <div class="flex items-center justify-between pb-2 border-b border-white/5">
              <span class="text-xs font-bold text-gray-400 uppercase tracking-widest">Deck Slides ({{ previewData.slides.length }})</span>
              <button @click="addNewSlide" class="text-xs font-bold text-orange-400 hover:text-orange-300 flex items-center gap-1">
                <span>+ Add Slide</span>
              </button>
            </div>

            <!-- Vertical Thumbnail List -->
            <div class="space-y-3 overflow-y-auto max-h-[460px] pr-1 scrollbar-custom">
              <div v-for="(slide, index) in previewData.slides" 
                   :key="index" 
                   class="slide-thumb rounded-xl p-3 flex flex-col justify-between text-left relative group cursor-pointer border-2 transition-all duration-300"
                   :class="index === selectedSlideIndex ? 'border-orange-500 shadow-md shadow-orange-500/5 scale-[1.01]' : 'border-white/5 hover:border-white/15'"
                   :style="{ background: previewData.theme.bg }"
                   style="min-height:92px;"
                   @click="selectedSlideIndex = index">
                
                <div class="w-full">
                  <div :style="{ background: previewData.theme.accent }" style="height:2px;width:15px;border-radius:1px;margin-bottom:4px;"></div>
                  <div :style="{ color: previewData.theme.text }" class="text-[9px] font-bold line-clamp-2 leading-tight opacity-90 pr-12">
                    {{ slide.title }}
                  </div>
                </div>
                
                <div class="flex items-center justify-between mt-2">
                  <span class="text-[9px] font-bold opacity-40" :style="{ color: previewData.theme.text }">0{{ index + 1 }}</span>
                  <span class="text-[8px] uppercase tracking-wider scale-90 opacity-50" :style="{ color: previewData.theme.accent }">{{ slide.type }}</span>
                </div>

                <!-- Hover actions: Reordering Arrow & Delete -->
                <div class="absolute right-2 top-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                  <button @click.stop="moveSlideUp(index)" :disabled="index === 0" class="w-5 h-5 bg-black/60 rounded flex items-center justify-center text-white hover:bg-black disabled:opacity-30">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg>
                  </button>
                  <button @click.stop="moveSlideDown(index)" :disabled="index === previewData.slides.length - 1" class="w-5 h-5 bg-black/60 rounded flex items-center justify-center text-white hover:bg-black disabled:opacity-30">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <button @click.stop="deleteSlide(index)" v-if="previewData.slides.length > 1" class="w-5 h-5 bg-red-600/90 rounded flex items-center justify-center text-white hover:bg-red-700">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';

// =============================================
// API BASE URL CONFIGURATION
// =============================================
const FLASK_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://127.0.0.1:5003'
  : '/python-api';

// =============================================
// DATA & STATE VARIABLES
// =============================================
const topic = ref('');
const templateStyle = ref('business');
const slidesCount = ref('10');
const additionalInstructions = ref('');
const speakerNotes = ref(false);
const professionalTone = ref(true);

const topicError = ref(false);
const isShaking = ref(false);
const currentState = ref('placeholder'); // placeholder | loading | preview

const loadingLabel = ref('Crafting your outline…');
const progressPercent = ref(0);
const selectedSlideIndex = ref(0);

const stepStatuses = reactive({
  step1: { opacity: '1', color: 'var(--mint)', icon: '✓' },
  step2: { opacity: '0.4', color: '', icon: '◌' },
  step3: { opacity: '0.4', color: '', icon: '◌' },
  step4: { opacity: '0.4', color: '', icon: '◌' }
});

const previewData = reactive({
  title: '',
  meta: '',
  theme: {},
  slides: []
});

const downloadStates = reactive({
  pptx: {
    html: '<span><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" style="display:inline; margin-right:4px;"><path d="M8 2v9M4 8l4 4 4-4M2 13h12"/></svg>Download PPTX</span>',
    disabled: false,
    opacity: '1'
  },
  pdf: {
    html: '<span><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" style="display:inline; margin-right:4px;"><path d="M4 2h6l4 4v9H4V2z"/><polyline points="10,2 10,6 14,6"/></svg>PDF</span>',
    disabled: false,
    opacity: '1'
  }
});

// =============================================
// COLOR THEMES CONFIGURATION
// =============================================
const templateThemes = {
  business: {
    bg: 'linear-gradient(135deg,#0F1E3C,#1A2E5A)',
    cardBg: 'rgba(24, 43, 80, 0.65)',
    accent: '#2DDBB0',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.55)',
    category: 'Business Professional'
  },
  startup: {
    bg: 'linear-gradient(135deg,#1F1010,#351A1A)',
    cardBg: 'rgba(48, 28, 28, 0.65)',
    accent: '#FF5C3B',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.55)',
    category: 'Startup Pitch'
  },
  education: {
    bg: 'linear-gradient(135deg,#1F1B35,#2D2650)',
    cardBg: 'rgba(45, 40, 72, 0.65)',
    accent: '#F5C843',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.55)',
    category: 'Education & Academic'
  },
  marketing: {
    bg: 'linear-gradient(135deg,#1A1F10,#252E18)',
    cardBg: 'rgba(38, 44, 25, 0.65)',
    accent: '#2DDBB0',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.55)',
    category: 'Marketing & Sales'
  },
  minimal: {
    bg: 'linear-gradient(135deg,#F0EDE7,#E8E2D6)',
    cardBg: '#faf8f5',
    accent: '#1a1a1f',
    text: '#1a1a1f',
    subtext: 'rgba(26,26,31,0.5)',
    category: 'Clean Minimal'
  },
  dark: {
    bg: 'linear-gradient(135deg,#050509,#10101A)',
    cardBg: 'rgba(16, 16, 24, 0.65)',
    accent: '#FF5C3B',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.45)',
    category: 'Dark Premium'
  }
};

// =============================================
// COMPUTED LAYOUT GRID COLUMNS
// =============================================
const gridColumnsClass = computed(() => {
  const count = previewData.slides[selectedSlideIndex.value]?.bullets?.length || 0;
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  return 'grid-cols-3';
});

// =============================================
// METHODS
// =============================================
const changeTheme = (key) => {
  templateStyle.value = key;
  previewData.theme = templateThemes[key] || templateThemes.business;
};

const generatePresentation = async () => {
  const trimmedTopic = topic.value.trim();

  // Validation
  if (!trimmedTopic) {
    topicError.value = true;
    isShaking.value = true;
    setTimeout(() => {
      isShaking.value = false;
    }, 500);
    return;
  }

  topicError.value = false;

  // Set loading states
  currentState.value = 'loading';
  progressPercent.value = 0;
  loadingLabel.value = 'Analyzing topic...';

  // Reset steps
  stepStatuses.step1 = { opacity: '1', color: 'var(--mint)', icon: '✓' };
  stepStatuses.step2 = { opacity: '0.4', color: '', icon: '◌' };
  stepStatuses.step3 = { opacity: '0.4', color: '', icon: '◌' };
  stepStatuses.step4 = { opacity: '0.4', color: '', icon: '◌' };

  // Progress animation interval
  const interval = setInterval(() => {
    if (progressPercent.value < 85) {
      progressPercent.value += 5;
    }
  }, 120);

  // Step timers
  setTimeout(() => {
    stepStatuses.step2 = { opacity: '1', color: 'var(--mint)', icon: '✓' };
    loadingLabel.value = 'Generating outline…';
  }, 500);

  setTimeout(() => {
    stepStatuses.step3 = { opacity: '1', color: 'var(--mint)', icon: '✓' };
    loadingLabel.value = 'Designing slides…';
  }, 1000);

  setTimeout(() => {
    stepStatuses.step4 = { opacity: '1', color: 'var(--mint)', icon: '✓' };
    loadingLabel.value = 'Applying template…';
  }, 1500);

  try {
    const response = await fetch(`${FLASK_URL}/generate-content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        topic: trimmedTopic,
        template: templateStyle.value,
        slides: slidesCount.value,
        additional_instructions: additionalInstructions.value,
        speaker_notes: speakerNotes.value,
        tone: professionalTone.value
      })
    });

    if (!response.ok) {
      throw new Error("Failed to generate content");
    }

    const slidesData = await response.json();
    
    clearInterval(interval);
    progressPercent.value = 100;

    setTimeout(() => {
      const theme = templateThemes[templateStyle.value] || templateThemes.business;
      previewData.title = trimmedTopic;
      previewData.meta = `${slidesData.length} slides · ${theme.category} · Styled Template`;
      previewData.theme = theme;
      previewData.slides = slidesData;
      
      selectedSlideIndex.value = 0;
      currentState.value = 'preview';
    }, 400);

  } catch (error) {
    console.error("Generator Error:", error);
    clearInterval(interval);
    alert("Python server connection error. Make sure python app.py is running on port 5003.");
    currentState.value = 'placeholder';
  }
};

// =============================================
// IN-PLACE SLIDE EDITING
// =============================================
const updateSlideTitle = (event, index) => {
  const newTitle = event.target.innerText.trim();
  if (newTitle) {
    previewData.slides[index].title = newTitle;
  }
};

const updateSlideBullet = (event, slideIdx, bulletIdx) => {
  const newBullet = event.target.innerText.trim();
  if (newBullet) {
    previewData.slides[slideIdx].bullets[bulletIdx] = newBullet;
  }
};

// =============================================
// SLIDE ORDER REORDERING & ACTIONS
// =============================================
const moveSlideUp = (index) => {
  if (index > 0) {
    const slide = previewData.slides.splice(index, 1)[0];
    previewData.slides.splice(index - 1, 0, slide);
    selectedSlideIndex.value = index - 1;
  }
};

const moveSlideDown = (index) => {
  if (index < previewData.slides.length - 1) {
    const slide = previewData.slides.splice(index, 1)[0];
    previewData.slides.splice(index + 1, 0, slide);
    selectedSlideIndex.value = index + 1;
  }
};

const addNewSlide = () => {
  previewData.slides.push({
    title: 'New Slide Title',
    type: 'content',
    bullets: ['Point details item 1', 'Point details item 2'],
    speaker_notes: ''
  });
  selectedSlideIndex.value = previewData.slides.length - 1;
};

const deleteSlide = (index) => {
  if (previewData.slides.length <= 1) return;
  previewData.slides.splice(index, 1);
  if (selectedSlideIndex.value >= previewData.slides.length) {
    selectedSlideIndex.value = previewData.slides.length - 1;
  }
};

const addBullet = (slideIdx) => {
  const slide = previewData.slides[slideIdx];
  if (slide.bullets.length < 5) {
    slide.bullets.push('New key detail item');
  } else {
    alert("Layout supports maximum 5 bullet items per slide.");
  }
};

const deleteBullet = (slideIdx, bulletIdx) => {
  const slide = previewData.slides[slideIdx];
  if (slide.bullets.length > 1) {
    slide.bullets.splice(bulletIdx, 1);
  } else {
    alert("A slide needs at least 1 key point.");
  }
};

// =============================================
// EXPORT COMPILATION
// =============================================
const exportToPptx = async () => {
  const origHtml = downloadStates.pptx.html;
  
  downloadStates.pptx.html = '<span>⬇ Compiling Slide Deck…</span>';
  downloadStates.pptx.disabled = true;
  downloadStates.pptx.opacity = '0.7';

  try {
    const response = await fetch(`${FLASK_URL}/export-pptx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slides: previewData.slides,
        template: templateStyle.value
      })
    });

    if (!response.ok) {
      throw new Error("PowerPoint generation failed");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const cleanName = previewData.title.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    link.download = `${cleanName || 'presentation'}.pptx`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    downloadStates.pptx.html = '<span>✓ PowerPoint Downloaded!</span>';
    downloadStates.pptx.opacity = '1';
    
    setTimeout(() => {
      downloadStates.pptx.html = origHtml;
      downloadStates.pptx.disabled = false;
    }, 1800);

  } catch (error) {
    console.error("PowerPoint export error:", error);
    alert("Connection to Python server failed. Verify python app.py is running on port 5003.");
    downloadStates.pptx.html = origHtml;
    downloadStates.pptx.disabled = false;
    downloadStates.pptx.opacity = '1';
  }
};

const fakeDownload = (type) => {
  const origHtml = downloadStates[type].html;
  
  const messages = {
    pdf:  '⬇ Generating PDF…'
  };
  const success = {
    pdf:  '✓ PDF Ready!'
  };

  downloadStates[type].html = `<span>${messages[type]}</span>`;
  downloadStates[type].disabled = true;
  downloadStates[type].opacity = '0.7';

  setTimeout(() => {
    downloadStates[type].html = `<span>${success[type]}</span>`;
    downloadStates[type].opacity = '1';
    
    setTimeout(() => {
      downloadStates[type].html = origHtml;
      downloadStates[type].disabled = false;
    }, 1800);
  }, 1200);
};

const resetTool = () => {
  topic.value = '';
  currentState.value = 'placeholder';
};
</script>

<style scoped>
/* Inline edits focus styling */
[contenteditable="true"]:focus {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(255, 92, 59, 0.35);
  border-radius: 4px;
  outline: none;
}

.scrollbar-custom::-webkit-scrollbar {
  width: 4px;
}
.scrollbar-custom::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.02);
}
.scrollbar-custom::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}
.scrollbar-custom::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

.spinner-small {
  border: 2px solid rgba(255, 255, 255, 0.1);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border-left-color: #ff5c3b;
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.animate-fade-in {
  animation: fadeIn 0.4s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
